import _ from 'lodash';
import PropTypes from 'prop-types';
import { combineReducers } from 'redux';
import { globalizeSelectors } from '../Utils';
import { groupAndReduce } from 'innovatint_base/common/ObjectUtils';
import {
  createArrayDataReducer,
  arrayDataType,
  createSingleDataReducer,
  clearResults,
} from '../factories/ApiCall';
import { statsDefaultDateRange } from '../../mylib/DateUtils';
import type { ApiCallState } from '../factories/ApiCall';
import type {
  FetchPosStatsPayload,
  FetchPurgePayload,
  PosStatisticsResponse,
  ProductStatsAggregate,
  PurgeStatisticsResponse,
  PurgeStatisticsRow,
  PurgedByDateRow,
  StatisticsAction,
  StatisticsColorantRow,
  StatisticsColourRow,
  StatisticsNameSelection,
  StatisticsProductRow,
  StatisticsState,
  StatisticsWeekdayRow,
} from '../types/Statistics';

/*
 * action types
 */

const types = {
  FETCH_PURGE: 'STATISTICS/FETCH_PURGE',
  FETCH_POS_STATS: 'STATISTICS/FETCH_POS_STATS',
  SET_PRODUCT_NAME: 'STATISTICS/SET_PRODUCT_NAME',
  SET_COLORANT_NAME: 'STATISTICS/SET_COLORANT_NAME',
  SET_COLORANT_NAME_FULFILLED: 'STATISTICS/SET_COLORANT_NAME_FULFILLED',
  SET_PRODUCT_NAME_FULFILLED: 'STATISTICS/SET_PRODUCT_NAME_FULFILLED',
} as const;

const mountPath = 'statistics'; // mount point in global state, must match root reducer

/*
 * action creators
 */

const actions = {
  setProductName: (
    label: string,
    value: string | null
  ): StatisticsAction<StatisticsNameSelection> => ({
    type: types.SET_PRODUCT_NAME_FULFILLED,
    payload: { label, value },
  }),
  setColorantName: (
    label: string,
    value: string | null
  ): StatisticsAction<StatisticsNameSelection> => ({
    type: types.SET_COLORANT_NAME_FULFILLED,
    payload: { label, value },
  }),
  fetchPurge: (
    start_date?: string,
    end_date?: string,
    machineid?: number
  ): StatisticsAction<FetchPurgePayload> => {
    const def = statsDefaultDateRange();
    return {
      type: types.FETCH_PURGE,
      payload: {
        start_date: start_date || def.start_date,
        end_date: end_date || def.end_date,
        machineid,
      },
    };
  },
  fetchPosStats: (
    start_date?: string,
    end_date?: string
  ): StatisticsAction<FetchPosStatsPayload> => ({
    type: types.FETCH_POS_STATS,
    payload: { start_date, end_date },
  }),
  clearPosStats: (): StatisticsAction => ({
    type: clearResults(types.FETCH_POS_STATS),
  }),
};

/*
 * state shape
 */
const propType = PropTypes.shape({
  purge: arrayDataType,
  pos_stats: PropTypes.shape({
    data: PropTypes.shape({
      daterange: PropTypes.arrayOf(PropTypes.string), // ISO dates
      colorants: PropTypes.arrayOf(
        PropTypes.shape({
          cntcode: PropTypes.string,
          cntvolsum: PropTypes.number,
        })
      ),
      products: PropTypes.arrayOf(
        PropTypes.shape({
          productname: PropTypes.string,
          basecode: PropTypes.string,
          cansizecode: PropTypes.string,
          cancount: PropTypes.number,
          basevolsum: PropTypes.number,
          pricesum: PropTypes.number,
        })
      ),
      weekdays: PropTypes.arrayOf(
        PropTypes.shape({
          weekday: PropTypes.number, // ISO weekday number, 1 = Monday, 7 = Sunday
          cancount: PropTypes.number,
          basevolsum: PropTypes.number,
          pricesum: PropTypes.number,
        })
      ),
      colours: PropTypes.arrayOf(
        PropTypes.shape({
          colourcode: PropTypes.string,
          cancount: PropTypes.number,
          basevolsum: PropTypes.number,
          pricesum: PropTypes.number,
        })
      ),
    }),
    pending: PropTypes.bool,
    error: PropTypes.string,
    productName: PropTypes.string,
  }),
});

/*
 * reducer
 */
const reducer = combineReducers({
  purge: createArrayDataReducer<PurgeStatisticsRow, FetchPurgePayload>(
    types.FETCH_PURGE
  ),
  pos_stats: createSingleDataReducer<
    PosStatisticsResponse,
    FetchPosStatsPayload
  >(types.FETCH_POS_STATS),
  productName: createSingleDataReducer<StatisticsNameSelection>(
    types.SET_PRODUCT_NAME,
    true,
    {
      label: 'All',
      value: null,
    }
  ),
  colorantName: createSingleDataReducer<StatisticsNameSelection>(
    types.SET_COLORANT_NAME,
    true,
    {
      label: 'All',
      value: null,
    }
  ),
}) as (
  state: StatisticsState | undefined,
  action: StatisticsAction
) => StatisticsState;

/*
 * helpers for selectors
 */

function productsStats(
  state: StatisticsState,
  productname: string | null = null
): StatisticsProductRow[] {
  const all = _.get(
    state.pos_stats,
    'data.products',
    []
  ) as StatisticsProductRow[];
  return productname == null
    ? all
    : all.filter((x) => x.productname === productname);
}

function aggeregateProductsStats(
  acc: ProductStatsAggregate = {
    basevolsum: 0,
    cancount: 0,
    pricesum: 0,
  },
  obj: StatisticsProductRow
): ProductStatsAggregate {
  acc.basevolsum += obj.basevolsum as number;
  acc.cancount += obj.cancount;
  acc.pricesum += obj.pricesum as number;
  return acc;
}

function groupSumProducts(
  statsArray: StatisticsProductRow[],
  keyName: keyof StatisticsProductRow
): Array<ProductStatsAggregate & Partial<StatisticsProductRow>> {
  return groupAndReduce(
    statsArray,
    keyName,
    aggeregateProductsStats as unknown as (
      acc: StatisticsProductRow | undefined,
      obj: StatisticsProductRow
    ) => StatisticsProductRow
  ) as Array<ProductStatsAggregate & Partial<StatisticsProductRow>>;
}

function colorantsStats(
  state: StatisticsState,
  source: string | null = null,
  cntcode: string | null = null
): StatisticsColorantRow[] {
  let data = _.get(
    state.pos_stats,
    'data.colorants',
    []
  ) as StatisticsColorantRow[];
  if (source != null) {
    data = data.filter((x) => x.source === source);
  }
  if (cntcode != null) {
    data = data.filter((x) => x.cntcode === cntcode);
  }
  return data;
}

function mapToDate(
  arr: PurgeStatisticsResponse
): Array<PurgeStatisticsRow & { date: string }> {
  return arr.map((x) => ({ ...x, date: x.datetime.slice(0, 10) }));
}

/*
 * selectors
 */
const localSelectors = {
  purge: (
    state: StatisticsState
  ): ApiCallState<PurgeStatisticsResponse, FetchPurgePayload> => state.purge,
  purged_cntcodes: (state: StatisticsState): string[] => {
    const res = _.uniq(state.purge.data.map((x) => x.cntcode));
    res.sort();
    return res;
  },
  purged_by_date: (state: StatisticsState): PurgedByDateRow[] =>
    groupAndReduce(mapToDate(state.purge.data), 'date', ((
      acc: PurgedByDateRow | undefined = { date: '', volumes: {} },
      obj: PurgeStatisticsRow
    ): PurgedByDateRow => {
      acc.volumes[obj.cntcode] = (acc.volumes[obj.cntcode] || 0) + obj.volume;
      return acc;
    }) as unknown as (
      acc: (PurgeStatisticsRow & { date: string }) | undefined,
      obj: PurgeStatisticsRow & { date: string }
    ) => PurgeStatisticsRow & { date: string }) as unknown as PurgedByDateRow[],
  purged_totals: (state: StatisticsState): Record<string, number> =>
    state.purge.data.reduce<Record<string, number>>((acc, obj) => {
      acc[obj.cntcode] = (acc[obj.cntcode] || 0) + obj.volume;
      return acc;
    }, {}),
  tinted_product_names: (state: StatisticsState): string[] =>
    _.uniq(
      productsStats(state)
        .map((x) => x.productname)
        .filter((name): name is string => Boolean(name))
    ),
  tinted_by_product: (
    state: StatisticsState,
    productname: string | null = null
  ): Array<ProductStatsAggregate & Partial<StatisticsProductRow>> =>
    groupSumProducts(productsStats(state, productname), 'productname'),
  tinted_by_base: (
    state: StatisticsState,
    productname: string | null = null
  ): Array<ProductStatsAggregate & Partial<StatisticsProductRow>> =>
    groupSumProducts(productsStats(state, productname), 'basecode'),
  tinted_by_cansize: (
    state: StatisticsState,
    productname: string | null = null
  ): Array<ProductStatsAggregate & Partial<StatisticsProductRow>> =>
    groupSumProducts(productsStats(state, productname), 'cansizecode'),
  tinted_by_weekday: (state: StatisticsState): StatisticsWeekdayRow[] =>
    (_.get(state.pos_stats, 'data.weekdays', []) as StatisticsWeekdayRow[]).map(
      (x) => ({
        ...x,
        basevolsum: x.basevolsum,
      })
    ),
  tinted_by_color: (state: StatisticsState): StatisticsColourRow[] =>
    (_.get(state.pos_stats, 'data.colours', []) as StatisticsColourRow[]).map(
      (x) => ({
        ...x,
        basevolsum: x.basevolsum,
      })
    ),
  colorants_stats: colorantsStats, // see redux\selectors\PriceCalc.js
  currentProduct: (state: StatisticsState): StatisticsNameSelection | null =>
    state.productName.data,
  currentColorant: (state: StatisticsState): StatisticsNameSelection | null =>
    state.colorantName.data,
};

const selectors = globalizeSelectors(localSelectors, mountPath);

export {
  types as actionTypes,
  actions as default,
  propType,
  selectors,
  reducer,
};
